import { Component, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { HtmlDragSupport } from 'buzzyears.ui';
import { AbstractWorkflowGraphDispatcherObserver } from '../abstract-workflow-graph-dispatcher-observer';
import {
  WorkflowGraphDispatcherAction,
  WorkflowGraphDispatcherActionType,
  WorkflowGraphDispatcherService
} from '../workflow-graph-dispatcher.service';
import { IPoint } from 'buzzyears.core';
import {
  WorkflowGraphVertexInletPointComponent,
  WorkflowGraphVertexOutletPointComponent
} from '../vertex/interaction-point';

@Component({
  selector: 'g[wf-graph-draw-edge]',
  template: `
    <svg:path *ngIf="active" [attr.d]="path" stroke-linecap="round" stroke-width="3"
              [attr.stroke-dasharray]="complete ? '0, 0' : '10, 10'"
              stroke="#50AE01"/>
  `
})
export class WorkflowGraphDrawEdgeComponent extends AbstractWorkflowGraphDispatcherObserver implements OnInit, OnDestroy {

  @Input() zoom: number;
  active = false;

  protected dragSupport: HtmlDragSupport;

  dragMove$: Subscription;
  dragFinish$: Subscription;

  fromPoint: IPoint;
  toPoint: IPoint;

  inlet: WorkflowGraphVertexInletPointComponent;
  outlet: WorkflowGraphVertexOutletPointComponent;

  constructor(dispatcherService: WorkflowGraphDispatcherService, protected renderer: Renderer2,) {
    super(dispatcherService);

    this.dragSupport = new HtmlDragSupport(renderer);

    this.dragMove$ = this.dragSupport.dragMove.subscribe(location => this.setLocation(location));
    this.dragFinish$ = this.dragSupport.dragFinish.subscribe(() => this.onDragFinish());
  }

  onDispatcherEvent(action: WorkflowGraphDispatcherAction) {
    switch (action.type) {
      case WorkflowGraphDispatcherActionType.ConnectionDrawInitiated:
        this.activate(action.originator, action.data);
        break;

      case WorkflowGraphDispatcherActionType.ConnectionDrawReceiverActivated:
        this.inlet = action.originator;
        break;

      case WorkflowGraphDispatcherActionType.ConnectionDrawReceiverDeactivated:
        this.inlet = null;
        break;
    }
  }

  get complete(): boolean {
    return !!this.inlet && !!this.outlet;
  }

  get path(): string {
    const fromPoint = this.fromPoint,
      toPoint = this.toPoint;

    if (fromPoint && toPoint) {
      const x1 = fromPoint.x,
        y1 = fromPoint.y,

        x2 = toPoint.x,
        y2 = toPoint.y;

      const dx = x1 - x2,
        dy = -(y1 - y2),
        radius = 8;

      let inRads = Math.atan2(dy, dx);
      inRads = inRads < 0 ? Math.abs(inRads) : 2 * Math.PI - inRads;

      return [
        'M', x1 - (radius * Math.cos(inRads)), y1 - (radius * Math.sin(inRads)),
        'L', x2 + (radius * Math.cos(inRads)), y2 + (radius * Math.sin(inRads))
      ].join(' ');
    }

    return ' ';
  }

  setLocation(location) {
    this.toPoint = location;
  }

  onDragFinish() {

    if (this.complete) {

      const formKey = this.outlet.vertex.key + (this.outlet.point.named ? (':' + this.outlet.point.key) : ''),
        toKey = this.inlet.vertex.key + (this.inlet.point.named ? (':' + this.inlet.point.key) : '');

      this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.ConnectionCreateRequest, this, {
        key: `${formKey}->${toKey}`,
        fromKey: this.outlet.vertex.key,
        toKey: this.inlet.vertex.key,
        inlet: this.inlet.point.named ? this.inlet.point.key : undefined,
        outlet: this.outlet.point.named ? this.outlet.point.key : undefined,
      });
    }

    this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.ConnectionDrawTerminated, this);

    this.active = false;
    this.outlet = null;
    this.inlet = null;
  }

  activate(uiPoint: WorkflowGraphVertexOutletPointComponent, event: MouseEvent) {

    const point = uiPoint.point,
      vertex = uiPoint.vertex,
      specification = uiPoint.specification;

    this.active = true;
    this.fromPoint = { x: vertex.x + specification.at.x, y: vertex.y + specification.at.y };
    this.toPoint = Object.assign({}, this.fromPoint);

    this.dragSupport.activate(event, <any>this.fromPoint, this.zoom);
    this.outlet = uiPoint;

    event.stopPropagation();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.dragSupport.deactivate();

    if (this.dragFinish$) {
      this.dragFinish$.unsubscribe();
      this.dragFinish$ = null;
    }

    if (this.dragMove$) {
      this.dragMove$.unsubscribe();
      this.dragMove$ = null;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
  }
}



// WEBPACK FOOTER //
// ./src/library/graph/edge/workflow-graph-draw-edge.component.ts